/* LOGGBOKA — dagens runde.
 *
 * ---------------------------------------------------------------------------
 * DEN ENE REGELEN SOM SLÅR ALLE ANDRE
 *
 * Det er tømrere og praktiske folk som fyller dette ut, på en telefon, i vind.
 * Ett minutt. Alt annet på denne siden er underordnet det.
 *
 * Derfor FORESLÅR MASKINEN OG MENNESKET BEKREFTER. Dagen kommer ferdig listet
 * opp fra det vi allerede har målt — båten som lå der, fôringen som stoppet,
 * timen som skilte seg ut — og hvert kort besvares med én tommel. Fritekst er
 * alltid valgfritt og aldri påkrevd noe sted. Å skrive er reserveløsningen,
 * ikke interaksjonen.
 *
 * ---------------------------------------------------------------------------
 * HVORFOR «INGENTING Å MELDE» ER EN KNAPP
 *
 * Det er raden som gjør hele opptegnelsen troverdig. Mattilsynets funn mot
 * Firda var at «velferdsscoring gjennomføres i ulik grad, og vi har ikke sett
 * noen skriftlig rutine for dette» — og en rutine dokumenteres av dagene den
 * ble kjørt, også de stille. En rolig dag uten signatur er ikke til å skille
 * fra en dag ingen åpnet appen.
 *
 * Derfor er knappen aldri deaktivert, heller ikke med kort som står åpne. En
 * runde ført ærlig med tre ubesvarte kort er en bedre opptegnelse enn en som
 * aldri ble ført fordi knappen var grå.
 *
 * ---------------------------------------------------------------------------
 * INGEN RING OG INGEN SCORE
 *
 * SiteLog argumenterer for ringen og har rett: den krever ett avgrenset,
 * daglig, høyere-er-bedre tall med en historie. Det finnes ikke her. En dag med
 * fire hendelser er ikke verre enn en med to — den er en annen dag. Å tegne en
 * score ville oppfunnet et tall og deretter fått folk til å styre etter det.
 *
 * Framdrift er Neuraths enhet: seks merker for seks kort, og de besvarte er
 * fylt. For å vise dobbelt så mye, bruk to symboler.
 *
 * ---------------------------------------------------------------------------
 * ETT KORT OM GANGEN, OG KØEN SYNLIG OVER DET
 *
 * Seks kort under hverandre gjorde to ting galt. Alt så like viktig ut -- og en
 * liste der alt er like viktig er en liste der ingenting er det -- og skjermen
 * krevde at man leste seg gjennom hele før man fikk gjort noe.
 *
 * Nå står det ett kort, og det er det som trenger et menneske MEST i dag.
 * Serveren rangerer, fordi den har tallene rangeringen hviler på: en
 * dødelighetstopp er stille og ligger øverst, et lasteskip på 1,4 km er
 * øredøvende og kommer ikke på skjermen i det hele tatt.
 *
 * Avtalen er merkene. De sier at det finnes fire til, uten å legge dem der, og
 * de fylles etter hvert. Ingen skal lure på om appen har glemt noe -- og «Se
 * alle» folder ut hele køen for den som vil se den likevel.
 *
 * «Hopp over» legger kortet bakerst framfor å fjerne det. Den som ikke vet
 * svaret nå skal komme videre uten å miste saken; et kort som forsvant fordi
 * noen var usikre er et hull i loggen.
 *
 * INGEN STREKK OVER DAGER, med vilje. Framdrift innenfor én dag er ekte og
 * avgrenset. En teller som sier «7 dager på rad» belønner knappetrykket og ikke
 * observasjonen, og det er nøyaktig feil insentiv å legge inn i en journal et
 * tilsyn skal kunne stole på.
 *
 * RØDT er der manualen har det, og et ubesvart kort er ikke en av de tre
 * tingene. Kortet bæres av posisjon og av ord. Rødt finnes på denne siden bare
 * på et avvik som har gått over fristen.
 */
import { useCallback, useMemo, useState } from 'react'

import {
  ApiError,
  loggbok, loggbokPath, reportUrl,
  type LoggbokDag, type LoggbokForslag, type LoggbokHendelse,
  type SiteHour,
} from '../api'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Svarrad } from '../components/Svarrad'
import { Timelyd } from '../components/Timelyd'
import { Link } from '../router'
import { useApi } from '../useApi'

/** Anleggets egen dato, ikke leserens.
 *
 *  Samme regel som resten av produktet: en logg over et sted føres i det
 *  stedets døgn. Serveren svarer med `on` i anleggets sone og den strengen er
 *  fasiten — dette formaterer den bare. Å bygge «i dag» av `new Date()` her
 *  ville gitt feil dag for den som sitter i en annen sone enn anlegget. */
function dagsNavn(iso: string, tz: string | null | undefined): string {
  const idag = new Date().toLocaleDateString('sv-SE',
    { timeZone: tz || undefined })
  if (iso === idag) return 'I dag'
  const d = new Date(`${iso}T12:00:00Z`)
  const igaar = new Date(`${idag}T12:00:00Z`)
  igaar.setUTCDate(igaar.getUTCDate() - 1)
  if (d.getTime() === igaar.getTime()) return 'I går'
  return d.toLocaleDateString('nb-NO',
    { weekday: 'long', day: 'numeric', month: 'long' })
}

function skift(iso: string, dager: number): string {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + dager)
  return d.toISOString().slice(0, 10)
}

function klokke(iso: string | null, tz: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('nb-NO',
    { hour: '2-digit', minute: '2-digit', timeZone: tz || undefined })
}

/** Beviset bak et kort, som én dempet linje.
 *
 *  Den finnes fordi en slutning må SE UT som en slutning. «AIS sier at en båt
 *  var her» og «hydrofonen din hørte fôringen stoppe» er ulik mengde bevis, og
 *  et menneske vekter dem forskjellig hvis det får lov. Å tegne dem likt ville
 *  fått loggboka til å arve noen andres feil og så få den signert. */
function bevis(f: LoggbokForslag): string {
  const e = f.evidence || {}
  const ut: string[] = []
  const num = (k: string) => (typeof e[k] === 'number' ? (e[k] as number) : null)

  const m = num('closest_m')
  if (m !== null) ut.push(m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`)
  const pts = num('n_points')
  if (pts !== null) ut.push(`${pts} posisjoner`)
  if (e.aqua_fleet === true) ut.push('havbruksflåten')
  const mins = num('minutes')
  if (mins !== null) ut.push(`${mins} min`)
  const agree = num('n_agree'); const could = num('n_could_hear')
  if (agree !== null && could !== null) ut.push(`${agree} av ${could} hydrofoner`)
  const dead = num('n_dead'); const normal = num('normal')
  if (dead !== null && normal !== null) ut.push(`${dead} mot ${normal} vanlig`)
  if (typeof e.basis === 'string') ut.push(String(e.basis).replace(/_/g, ' '))
  if (typeof e.subtitle === 'string' && e.subtitle) ut.push(String(e.subtitle))
  const kb = num('size_bytes')
  if (kb !== null) ut.push(`PDF · ${Math.round(kb / 1024)} kB`)
  if (f.source === 'hour_vessels' && m === null) ut.push('AIS')
  return ut.join(' · ')
}

/** Spørsmålet kortet stiller, i klartekst over svarene.
 *
 *  KORTET HADDE INGEN OPPFORDRING. Overskriften var en observasjon -- «ANDREAS»
 *  lå ved anlegget 10:00-21:00 -- og under den lå seks like grå ruter som så ut
 *  som filtre. Ingenting på kortet sa hva man skulle gjøre, og et kort ingen vet
 *  hva de skal gjøre med er et kort som blir stående.
 *
 *  Så kortet er nå et SPØRSMÅL med et svar under. Det er den ene setningen som
 *  gjør knapperaden til en handling og ikke til en meny, og den skrives her og
 *  ikke i katalogen med vilje: spørsmålet er hvordan denne skjermen ber om
 *  svaret, mens katalogen eier hva svaret betyr. En tekstendring i tonefall skal
 *  ikke kreve en utrulling av tre tjenester.
 *
 *  Reserven er «Hva skjedde?». Den passer på alt, og et nytt korttype fra
 *  serveren skal aldri gi en tom linje. */
const SPORSMAAL: Record<string, string> = {
  fartoy: 'Hva gjorde båten?',
  foringsstopp: 'Hvorfor stoppet fôringen?',
  time: 'Hva var lyden?',
  klipp: 'Hva var lyden?',
  registeroperasjon: 'Stemmer det?',
  dodelighet: 'Vet dere hvorfor?',
  rapport: 'Er rapporten lest?',
}
const sporsmaal = (kind: string) => SPORSMAAL[kind] || 'Hva skjedde?'

/** Køen, som merker. Ikke en prosent.
 *
 *  Neurath: for å vise dobbelt så mye, bruk to symboler, aldri ett dobbelt så
 *  stort. Fem kort er fem merker, og de besvarte er fylt.
 *
 *  DEN GJØR TO JOBBER OG DET ER MENINGEN. Den er framdrift -- du ser at det går
 *  framover -- og den er innholdsfortegnelsen: den sier at det finnes fire til
 *  uten å legge dem på skjermen. Det er hele avtalen bak å vise ett kort om
 *  gangen. Ingen skal lure på om appen har glemt noe.
 */
function Ko({ svart, alle }: { svart: number; alle: number }) {
  if (!alle) return null
  return (
    <p className="loggbok-ko">
      <span className="loggbok-merker" aria-hidden="true">
        {Array.from({ length: alle }, (_, i) => (
          <i key={i} className={i < svart ? 'fylt' : undefined} />
        ))}
      </span>
      <span className="muted">
        {svart >= alle ? 'Alt er besvart' : `${svart} av ${alle} besvart`}
      </span>
    </p>
  )
}

/** Merdene, som ringer. Alle er valgt til å begynne med.
 *
 *  DEN VAR EN UTFOLDING OG ER DET IKKE LENGER. «Merd eller notat» skjulte de
 *  to feltene bak et trykk, og et felt bak et trykk er et felt som ikke fylles
 *  ut -- dessuten var det panelet som fikk folk til å lete etter en
 *  lagreknapp: man åpner noe, skriver i det, og ser seg om etter noe å trykke
 *  på. Nå står alt på kortet med én gang, og da er det ingenting å åpne og
 *  ingenting å bekrefte.
 *
 *  RINGER OG IKKE RUTER, fordi det er ringer. Tallet i en sirkel er merd 4 for
 *  den som står på flåten; en rektangulær knapp merket «4» er et filter. Formen
 *  er gratis og den er den samme figuren som Farm-siden tegner anlegget med.
 *
 *  ALLE VALGT FRA START, fordi det er det vanlige svaret. En båt lå ved
 *  ANLEGGET, ikke ved merd 4 -- så standarden må være hele anlegget, og den som
 *  vet bedre snevrer inn. Motsatt vei ville krevd et trykk av alle for å si det
 *  som var sant uansett.
 *
 *  «ALLE» ER TILSTANDSVISEREN. Når alt er valgt lyser «Alle» og ringene ligger
 *  rolig -- standarden skal ikke rope, og sju knallgrønne ringer ville vært
 *  kortets kraftigste figur foran svarraden, som er den som faktisk fører noe.
 *  Snevrer man inn, slukner «Alle» og de valgte ringene lyser: DA er utvalget
 *  en avgjørelse, og en avgjørelse skal synes.
 *
 *  Den siste ringen kan ikke slås av. «Dette skjedde i ingen merd» betyr
 *  ingenting, og «Alle» står rett ved siden av som veien tilbake. */
function Merdvelger({ pens, valgt, onValg }: {
  pens: { pen_id: string; pen_no: string }[]
  valgt: string[]
  onValg: (ids: string[]) => void
}) {
  if (!pens.length) return null
  const alle = valgt.length >= pens.length
  return (
    /* Én rad som ruller sidelengs, ikke en som brytes. Samme grep som
       hydrofonvelgeren, av samme grunn den ble skrevet for: ti merder brutt
       over tre linjer dytter svarraden under folden på en telefon, og
       svarraden er hele kortet. */
    <div className={`loggbok-merdvalg${alle ? ' alle' : ''}`}
      role="group" aria-label="Merder">
      <span className="loggbok-merdlapp">Merd</span>
      <button type="button" className={`merdring alle${alle ? ' paa' : ''}`}
        aria-pressed={alle}
        onClick={() => onValg(pens.map((p) => p.pen_id))}>Alle</button>
      {pens.map((p) => {
        const paa = valgt.includes(p.pen_id)
        return (
          <button key={p.pen_id} type="button"
            className={`merdring${paa ? ' paa' : ''}`}
            aria-pressed={paa} aria-label={`Merd ${p.pen_no}`}
            onClick={() => {
              if (!paa) return onValg([...valgt, p.pen_id])
              if (valgt.length <= 1) return
              onValg(valgt.filter((x) => x !== p.pen_id))
            }}>{p.pen_no}</button>
        )
      })}
    </div>
  )
}

/** Notatet. Alltid åpent, aldri påkrevd.
 *
 *  Uten etikett over seg -- plassholderen sier det samme og koster ikke en
 *  linje. To rader er nok til en setning, og en setning er alt noen skriver
 *  ved merdkanten. */
function Notatfelt({ verdi, onVerdi }: {
  verdi: string
  onVerdi: (v: string) => void
}) {
  return (
    <textarea className="loggbok-notat" rows={2} value={verdi}
      aria-label="Notat" placeholder="Notat (valgfritt)"
      onChange={(e) => onVerdi(e.target.value)} />
  )
}

/** Hvilke merder som skal føres, slik loggboka mener det.
 *
 *  Tom liste betyr HELE ANLEGGET og ikke «ingen merd» -- det er kontrakten
 *  `journal.entry_pens` er bygget på. Så når alle ringene står på, sendes
 *  ingen: «båten lå ved anlegget» er én påstand, ikke sju. */
const merdliste = (valgt: string[], pens: unknown[]) =>
  valgt.length >= pens.length ? [] : valgt

interface Sak {
  travel: boolean
  feil: ApiError | Error | null
}

export function SiteLoggbok({ id }: { id: string }) {
  const [dato, setDato] = useState<string | null>(null)
  const q = useApi<LoggbokDag>(loggbokPath(id, dato ?? undefined))
  const d = q.data

  const [sak, setSak] = useState<Sak>({ travel: false, feil: null })
  const [aapen, setAapen] = useState<string | null>(null)
  const [skriver, setSkriver] = useState(false)
  /* Hoppet over, i denne økta. Bakerst i køen framfor borte: den som ikke kan
     svare på et kort nå skal komme videre uten å miste det, og et kort som
     forsvant fordi noen ikke visste svaret er et hull i loggen. Ikke lagret --
     i morgen er det en ny runde, og et kort man hoppet over i går skal møte en
     med friske øyne. */
  const [hoppet, setHoppet] = useState<string[]>([])
  const [alle, setAlle] = useState(false)

  const on = d?.on ?? dato ?? ''
  const tz = d?.timezone

  /** Hver skriving svarer med hele dagen, så skjermen settes fra svaret og
   *  aldri gjettes. Feilen holdes her og vises der handlingen skjedde. */
  const gjor = useCallback(async (fn: () => Promise<LoggbokDag>) => {
    setSak({ travel: true, feil: null })
    try {
      const neste = await fn()
      q.patch(() => neste)
      setSak({ travel: false, feil: null })
      return true
    } catch (e) {
      setSak({ travel: false,
               feil: e instanceof ApiError ? e : new Error('nettverk') })
      return false
    }
  }, [q])

  /* Køen. Serveren har alt sortert viktigst først -- rekkefølgen er den samme
     for alle som åpner dagen -- så det eneste som skjer her er at det man har
     hoppet over legges bakerst. */
  const aapne = useMemo(() => {
    const åpne = (d?.proposals ?? []).filter((p) => p.state === 'open')
    const vent = åpne.filter((p) => !hoppet.includes(p.proposal_id))
    const bak = åpne.filter((p) => hoppet.includes(p.proposal_id))
    return [...vent, ...bak]
  }, [d, hoppet])
  const lukkede = useMemo(
    () => (d?.proposals ?? []).filter((p) => p.state !== 'open'),
    [d])

  if (q.cold) return <Rows n={4} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!d) return null

  const ferdig = !!d.round.completed_at

  return (
    <>
      {/* «Skriv noe selv» hører hjemme oppe til høyre, ikke nederst.
          Den er sidens sekundære vei -- reserveløsningen for det maskinen ikke
          foreslo -- og nederst sto den som sidens siste og dermed tyngste ord,
          rett over «Ferdig med runden». To knapper på rad nederst, og den ene
          er en avslutning og den andre en start. Oppe til høyre er den der en
          verktøyknapp hører hjemme: tilgjengelig hele tiden, aldri i veien for
          kortet, og aldri forvekslet med å avslutte runden. */}
      <PageHead title="Loggbok" tight
        lead={`${dagsNavn(on, tz)} · ${aapne.length
          ? `${aapne.length} å svare på` : 'ingenting å svare på'}`}
        cta={d.available && !skriver
          ? (
            <button type="button" className="btn loggbok-nytt"
              onClick={() => setSkriver(true)}>+ Skriv noe selv</button>
          )
          : undefined} />

      <div className="loggbok-faner">
        <button type="button" className="btn sm primary" aria-current="page">
          I dag
        </button>
        <Link className="btn sm ghost" to={`/sites/${encodeURIComponent(id)}/loggbok/uke`}>
          Uka
        </Link>
        <span className="grow" />
        <div className="loggbok-dagnav">
          <button type="button" className="btn sm ghost" aria-label="Forrige dag"
            onClick={() => setDato(skift(on, -1))}>‹</button>
          {dato && (
            <button type="button" className="btn sm ghost"
              onClick={() => setDato(null)}>I dag</button>
          )}
          <button type="button" className="btn sm ghost" aria-label="Neste dag"
            disabled={dagsNavn(on, tz) === 'I dag'}
            onClick={() => setDato(skift(on, 1))}>›</button>
        </div>
      </div>

      {/* Loggboka er ikke der. Sagt som en setning og ikke som en tom runde:
          et blankt felt som betyr «ingenting skjedde» og et som betyr «vi fikk
          ikke spurt» må aldri se like ut. */}
      {!d.available && (
        <p className="notice">
          {d.reason || 'Loggboka er ikke tilgjengelig akkurat nå.'}
        </p>
      )}
      {d.stale && (
        <p className="notice">
          Viser sist lagrede utgave. Nye svar kan ikke lagres før loggboka
          svarer igjen.
        </p>
      )}

      {sak.feil && <ErrorBox error={sak.feil} />}

      <Ko svart={d.counts.answered + d.counts.dismissed}
        alle={d.proposals.length} />

      {d.available && (
        <section className="stack">
          {/* Skjemaet folder ut ØVERST, under knappen som åpnet det. Et panel
              som dukker opp under seks kort er et panel man ikke ser at man
              åpnet. */}
          {skriver && (
            <Skriv d={d} id={id} on={on} travel={sak.travel} gjor={gjor}
              onLukk={() => setSkriver(false)} />
          )}

          {/* ETT KORT OM GANGEN.
              En liste der alt er like viktig er en liste der ingenting er det,
              og seks kort på én skjerm er en vegg man må lese seg gjennom før
              man får gjort noe. Det øverste er det som trenger et menneske mest
              i dag -- serveren har rangert -- og merkene over sier at resten
              finnes. «Se alle» er der for den som vil ha oversikten likevel. */}
          {aapne.length > 0 && !alle && (
            /* KORTSTOKKEN. Det neste kortet stikker fram under det øverste.
               «2 saker igjen etter denne» sto som en setning nederst på kortet
               og ble ikke lest: den lå etter handlingen, i dempet grå, på
               plassen der et kort ellers er slutt. Skjermen så ut som én
               oppgave, og merkene over den er små og leses som pynt.
               En bunke ark leses derimot som en bunke uten at noen forklarer
               den, og den viser ORDET fra neste sak -- så det som ligger under
               er en konkret ting og ikke et antall. Den er trykkbar av samme
               grunn: det man ser kanten av, vil man dra fram. */
            <div className="loggbok-dekk">
              <Kort key={aapne[0]!.proposal_id} f={aapne[0]!} d={d} id={id}
                on={on} tz={tz} travel={sak.travel} gjor={gjor}
                onHopp={aapne.length > 1
                  ? () => setHoppet([...hoppet.filter(
                      (x) => x !== aapne[0]!.proposal_id), aapne[0]!.proposal_id])
                  : undefined} />

              {aapne.length > 1 && (
                <button type="button" className="loggbok-neste"
                  aria-label={`${aapne.length - 1} ${aapne.length === 2
                    ? 'sak' : 'saker'} igjen. Neste: ${aapne[1]!.headline
                    }. Vis alle.`}
                  onClick={() => setAlle(true)}>
                  <span className="loggbok-naar" aria-hidden="true">
                    {klokke(aapne[1]!.occurred_at, tz)}
                  </span>
                  <span className="loggbok-nestetekst" aria-hidden="true">
                    {aapne[1]!.headline}
                  </span>
                </button>
              )}
              {/* Tredje arket, som en kant. Bare når det finnes flere enn to --
                  en bunke på to ser ut som to ark, og et falskt lag under er en
                  figur som lover mer enn det er. */}
              {aapne.length > 2 && (
                <span className="loggbok-kant" aria-hidden="true" />
              )}
            </div>
          )}

          {aapne.length > 0 && alle && aapne.map((f) => (
            <Kort key={f.proposal_id} f={f} d={d} id={id} on={on} tz={tz}
              travel={sak.travel} gjor={gjor} />
          ))}

          {aapne.length > 1 && (
            <button type="button" className="btn ghost sm loggbok-sealle"
              aria-expanded={alle} onClick={() => setAlle(!alle)}>
              {alle
                ? 'Vis én om gangen'
                : `Se alle ${aapne.length} — ${aapne.length - 1} igjen etter denne`}
            </button>
          )}

          {!aapne.length && (
            d.proposals.length
              ? <RoligDag tittel="Ferdig."
                  under={`Alle ${d.proposals.length} sakene er besvart.`} />
              : <RoligDag tittel="Rolig dag."
                  under="Ingenting har skilt seg ut i sjøen i dag." />
          )}

        </section>
      )}

      {(d.entries.length > 0 || lukkede.length > 0) && (
        <section className="seksjon">
          <h2 className="seksjon-hode">Dagen så langt</h2>
          {d.entries.map((e) => (
            <Hendelse key={e.entry_id} e={e} d={d} id={id} on={on} tz={tz}
              aapen={aapen === e.entry_id}
              onAapne={() => setAapen(aapen === e.entry_id ? null : e.entry_id)}
              travel={sak.travel} gjor={gjor} />
          ))}
          {lukkede.filter((p) => p.state === 'dismissed').map((p) => (
            <p key={p.proposal_id} className="loggbok-kvittering muted">
              {p.headline} <em>— ingenting</em>
            </p>
          ))}
        </section>
      )}

      {/* Ført et annet sted. Lenket, aldri gjentatt: en Laksvel-runde og en
          lusetelling eies av sine egne sider, og å kopiere dem hit er hvordan
          et anlegg ender med to tall for én morgen. */}
      {d.elsewhere.length > 0 && (
        <section className="seksjon">
          <h2 className="seksjon-hode">Ført et annet sted</h2>
          {d.elsewhere.map((x) => (
            <p key={x.kind} className="loggbok-annetsted">
              <Link to={x.href}>{x.text}</Link>
            </p>
          ))}
        </section>
      )}

      {d.available && (
        <div className="loggbok-avslutt">
          {ferdig
            ? (
              <p className="muted">
                Runden er ført {klokke(d.round.completed_at, tz)}
                {d.round.n_open ? ` — ${d.round.n_open} kort står åpne.` : '.'}
              </p>
            )
            : (
              <button type="button" className="btn primary" disabled={sak.travel}
                onClick={() => gjor(() => loggbok.runde(id, { on_date: on }))}>
                {d.proposals.length || d.entries.length
                  ? 'Ferdig med runden'
                  : 'Ingenting å melde'}
              </button>
            )}
        </div>
      )}
    </>
  )
}

/** En rolig dag, tegnet.
 *
 * ---------------------------------------------------------------------------
 * HVA DEN IKKE SIER
 *
 * Den sier IKKE at fisken har det bra. Vi vet at ingenting skilte seg ut i
 * lyden, at ingen båt kom nær nok, og at fôringen gikk -- og ingen av delene er
 * en velferdspåstand. Hele resten av produktet er bygget på at fravær av funn
 * ikke er funn av fravær, og en glad fisk med teksten «alt er bra» ville brutt
 * den regelen på den ene skjermen folk ser oftest.
 *
 * Så fisken er varme, ikke bevis. Ordene under den sier nøyaktig hva vi vet:
 * ingenting har skilt seg ut. Det er en fin ting å få vite, og det er sant.
 *
 * ---------------------------------------------------------------------------
 * HVORFOR DEN LIKEVEL SKAL VÆRE DER
 *
 * De fleste dagene på et velfungerende anlegg ER tomme, og en tom dag er den
 * vanligste tilstanden dette produktet har. «Ingenting er lagt merke til i
 * dag.» i grå tekst leses som at noe mangler -- som om siden ikke virket, eller
 * som om man hadde glemt noe. Det er feil følelse å gi noen som nettopp har
 * gjort jobben sin.
 *
 * Merkevaren er 85 % dyp, 12 % signal, 3 % puls, og tilbakeholdenheten ER
 * merkevaren -- så dette er én strøket figur i signalgrønt, ikke en
 * illustrasjon. Bevegelsen er langsom og slås av under
 * `prefers-reduced-motion`, som alt annet som beveger seg her.
 */
function RoligDag({ tittel, under }: { tittel: string; under: string }) {
  return (
    <div className="rolig">
      <svg className="rolig-fisk" viewBox="0 0 240 128" aria-hidden="true"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <g className="rolig-driv">
          {/* Kroppen: én sammenhengende linje fra snuten, over ryggen, ned til
              halerota og tilbake langs buken. Alle finner under er festet til
              PUNKTER PÅ DENNE KURVEN, regnet ut på den, ikke plassert på
              øyemål -- en finne som svever er det øyet ser først. */}
          <path d="M26 64C46 32 94 24 140 40C158 46 170 52 180 60
                   C170 70 158 77 140 82C92 98 44 94 26 64Z" />
          {/* Halefinnen, kløftet. Festet i TO punkter på kroppen -- (174,56)
              på ryggsiden og (174,65) på buksiden -- fordi en hale som henger
              i ett punkt henger i løse lufta, og det er nettopp det øyet
              fester seg ved. Begge punktene er regnet ut på kurven over. */}
          <path d="M174 56c14-8 27-14 36-16-5 9-7 16-7 25s2 16 7 25
                   c-9-2-22-8-36-16" />
          {/* Ryggfinnen: fra (88,32) til (123,35), begge på ryggen. */}
          <path d="M88 32c6-13 16-19 27-19-3 8-4 14-2 22" />
          {/* Fettfinnen, den lille foran halen. Laksens kjennetegn. */}
          <path d="M155 45c4-6 9-9 13-9-2 5-3 8-3 12" />
          {/* Brystfinnen, festet på buken bak gjellebuen. */}
          <path d="M58 87c4 10 11 16 19 18-4-7-5-13-3-19" />
          {/* Gattfinnen. */}
          <path d="M104 90c3 9 8 14 14 15-3-6-4-10-3-14" />
          {/* Gjellebue og øye. */}
          <path d="M50 45c-5 11-5 23 0 34" />
          <circle cx="41" cy="60" r="2.6" fill="currentColor" stroke="none" />
        </g>
        {/* Bobler. To, langsomme, forskjøvet -- nok til at bildet lever,
            for få til at det ber om oppmerksomhet. */}
        <circle className="rolig-boble b1" cx="33" cy="46" r="2.5" />
        <circle className="rolig-boble b2" cx="45" cy="40" r="1.8" />
      </svg>
      <p className="rolig-tittel">{tittel}</p>
      <p className="muted">{under}</p>
    </div>
  )
}

/** Å høre på timen kortet handler om.
 *
 *  ET LYDKORT MÅ KUNNE HØRES. «Kl. 00:00 skilte seg ut i sjøen» er en påstand
 *  fra en analyse, og det eneste svaret på «hva var det?» som ikke er en gjetning
 *  er å høre etter. Uten avspilling er kortet et spørsmål stilt til noen som
 *  ikke har fått se beviset -- og svarene ville vært verdiløse i en logg.
 *
 *  Timen hentes først når noen trykker Hør. Den er det tyngste kallet på siden
 *  -- spektre per sensor, minutter, AIS-spor -- og en dagsvisning med seks kort
 *  som lastet alle sammen ville brukt en 4G-forbindelse på seks ting ingen har
 *  bedt om ennå.
 *
 *  SPEKTERET ER MED VILJE IKKE HER. Loggen tegner det, og argumenterer godt for
 *  hvorfor: du kan ikke høre en skipstone på 32 Hz i en laptop. Men loggboka
 *  spør «hva gjorde dere med det», ikke «hva var det egentlig» -- og den som vil
 *  etterprøve påstanden har en lenke til loggen rett under. Ett spørsmål per
 *  side.
 */
function Hor({ id, hour }: { id: string; hour: number }) {
  const q = useApi<SiteHour>(
    `/v1/sites/${encodeURIComponent(id)}/hours/${hour}`)

  if (q.cold) return <Rows n={2} />
  if (q.error) return <ErrorBox error={q.error} />
  if (!q.data) return null

  return (
    <div className="loggbok-lyd">
      <Timelyd media={q.data.media} sensors={q.data.sensors}
        calibration={q.data.calibration} straks />
      <p className="muted">
        <Link to={`/sites/${encodeURIComponent(id)}/logg`}>
          Se timen i loggen
        </Link>{' '}— med spekter, peiling og båtene rundt.
      </p>
    </div>
  )
}

/** Ett kort: overskrift i ord, én dempet beviselinje, og en rad å trykke på. */
function Kort({ f, d, id, on, tz, travel, gjor, onHopp }: {
  f: LoggbokForslag
  d: LoggbokDag
  id: string
  on: string
  tz: string | null | undefined
  travel: boolean
  gjor: (fn: () => Promise<LoggbokDag>) => Promise<boolean>
  /** Legg denne bakerst. Utelatt når den er den siste -- å hoppe over det
   *  eneste kortet er å trykke på en knapp som ikke gjør noe. */
  onHopp?: () => void
}) {
  const [merder, setMerder] = useState<string[]>(
    () => d.pens.map((p) => p.pen_id))
  const [horer, setHorer] = useState(false)
  const [notat, setNotat] = useState('')
  const linje = bevis(f)

  /* Hvilket skip kortet handler om, når det handler om ett. AIS-kortene bærer
     det i beviset; ingen andre gjør det, og et kart uten en båt å følge er et
     satellittbilde med en rød prikk på. */
  const mmsi = typeof f.evidence?.mmsi === 'number'
    ? (f.evidence.mmsi as number) : null
  const kanSe = f.source === 'hour_vessels' && mmsi !== null

  /* Dokumentet kortet handler om. En rapport er ført i loggboka på dagen den
     handler om, ikke dagen den ble skrevet; lenka går gjennom samme vindu som
     loggsida bruker, så innlogging kreves hele dokumentets levetid. */
  const slug = f.kind === 'rapport' && typeof f.evidence?.slug === 'string'
    ? (f.evidence.slug as string) : null

  /* Hvilken time kortet peker på, når det peker på en. `hour_epoch` settes av
     lydkortene; et klipp bærer sitt eget starttidspunkt i stedet, og rundes ned
     til timen det ligger i -- det er den timen arkivet er delt opp i. */
  const time = typeof f.evidence?.hour_epoch === 'number'
    ? (f.evidence.hour_epoch as number)
    : (f.occurred_at ? Math.floor(Date.parse(f.occurred_at) / 3_600_000) * 3600
       : null)
  const kanHore = time !== null
    && (f.source === 'hour_anomaly' || f.source === 'clips')

  const svar = (key: string) => gjor(() => loggbok.skriv(id, {
    on_date: on,
    kind: f.kind,
    answer: key,
    occurred_at: f.occurred_at,
    proposal_ids: [f.proposal_id],
    pen_ids: merdliste(merder, d.pens),
    note: notat.trim() || null,
    /* Skipet blir med inn i det som føres. Uten det kan en ført hendelse ikke
       åpne sitt eget spor igjen -- og en avlusing man ikke kan se på i
       ettertid er nettopp den dokumentasjonen tilsynet etterlyser. */
    detail: mmsi !== null ? { mmsi } : {},
  }))

  return (
    <article className="card loggbok-kort">
      <p className="loggbok-naar">{klokke(f.occurred_at, tz)}</p>
      <h3>{f.headline}</h3>
      {linje && <p className="loggbok-bevis muted">{linje}</p>}

      {/* Hør før du svarer. Over svarene, fordi rekkefølgen er den man faktisk
          jobber i: lytt, så bestem. */}
      {kanHore && (
        <div className="loggbok-horrad">
          <button type="button" className={`btn sm${horer ? ' primary' : ''}`}
            aria-expanded={horer} onClick={() => setHorer(!horer)}>
            {horer ? 'Skjul lyden' : 'Hør'}
          </button>
        </div>
      )}
      {kanHore && horer && <Hor id={id} hour={time!} />}

      {/* Se før du svarer, av samme grunn som «Hør» står over svarene: man ser
          etter, og så bestemmer man.

          EN LENKE OG IKKE EN UTFOLDING. Et anlegg er tre hundre meter på skrå,
          og i et kort ble Ljøsøys sju ringer på størrelse med en fingernegl --
          `Kart.tsx` skrev argumentet først og det gjelder ordrett her. Svarene
          er med på den andre sida, så turen er ferdig der. */}
      {slug && (
        <div className="loggbok-horrad">
          <a className="btn sm" href={reportUrl(id, slug)} target="_blank"
            rel="noopener">Åpne rapporten</a>
          {' '}
          <a className="btn sm ghost" href={reportUrl(id, slug, true)}>Last ned</a>
        </div>
      )}

      {kanSe && (
        <div className="loggbok-horrad">
          <Link className="btn sm"
            to={`/sites/${encodeURIComponent(id)}/loggbok/spor?on=${
              encodeURIComponent(on)}&mmsi=${mmsi}`}>
            Se sporet
          </Link>
        </div>
      )}

      {/* MERD OG NOTAT LIGGER FRAMME. Ingen utfolding, ingenting å åpne.

          Panelet var to trykk unna det man skulle: ett for å åpne og ett for
          å skrive. Det er slik et valgfritt felt blir et felt som aldri
          fylles ut -- og det var panelet som fikk folk til å lete etter en
          lagreknapp, fordi å åpne noe, skrive i det og så se seg om er
          bevegelsen et skjema lærer deg. Flatt kort, ingen dør, ingen
          bekreftelse: det man ser er det som blir ført.

          Rekkefølgen er den man jobber i -- se på beviset, si hvor, si hva du
          vet, svar -- og svaret er kortets siste ord. */}
      <Merdvelger pens={d.pens} valgt={merder} onValg={setMerder} />
      <Notatfelt verdi={notat} onVerdi={setNotat} />

      {/* Spørsmålet, og så svaret. Kortets eneste oppfordring.

          Raden er den ENESTE handlingen som fører noe, og den så ut som et
          filter: seks grå ruter i 13 px, samme farge som beviset over dem. Nå
          er den kortets tyngste ledd -- full trykkhøyde, samme farge som
          brødteksten -- og alt annet på kortet er dempet ghost. «Hopp over» og
          «Ingenting» er utveier, ikke valg, og skal se slik ut. */}
      <p className="loggbok-sporsmaal">
        {sporsmaal(f.kind)}
        {' '}<span className="loggbok-lagrer">Ett trykk lagrer</span>
      </p>
      <Svarrad svar={f.answers} laast={travel} onSvar={svar} />

      <div className="loggbok-kortbunn">
        {onHopp && (
          <button type="button" className="btn sm ghost" onClick={onHopp}>
            Hopp over
          </button>
        )}
        <span className="grow" />
        <button type="button" className="btn sm ghost" disabled={travel}
          onClick={() => gjor(() => loggbok.avvis(id, f.proposal_id,
            { on_date: on }))}>Ingenting</button>
      </div>

    </article>
  )
}

/** Det som allerede står ført, som én linje hver, utvidbar. */
function Hendelse({ e, d, id, on, tz, aapen, onAapne, travel, gjor }: {
  e: LoggbokHendelse
  d: LoggbokDag
  id: string
  on: string
  tz: string | null | undefined
  aapen: boolean
  onAapne: () => void
  travel: boolean
  gjor: (fn: () => Promise<LoggbokDag>) => Promise<boolean>
}) {
  const [kommentar, setKommentar] = useState('')
  /* Skipet raden ble ført om, hvis den ble ført om ett. Kortet la det i
     `detail` da svaret ble gitt, slik at en ført hendelse kan åpne sitt eget
     spor igjen -- det er den samme dokumentasjonen en uke etterpå som den var
     i det øyeblikket. */
  const mmsi = typeof e.detail?.mmsi === 'number'
    ? (e.detail.mmsi as number) : null
  const kat = d.katalog
  const navn = kat.forslag[e.kind]?.navn ?? kat.skrevet[e.kind]?.navn ?? e.kind
  const svar = [...(kat.forslag[e.kind]?.svar ?? []),
                ...(kat.skrevet[e.kind]?.svar ?? [])]
    .find((s) => s.key === e.answer)?.label

  const merder = e.scope === 'site'
    ? 'hele anlegget'
    : e.pens.map((p) => `merd ${p.pen_no}`).join(', ')

  return (
    <article className={`loggbok-rad${e.severity === 'avvik' ? ' avvik' : ''}`}>
      <button type="button" className="loggbok-radhode" aria-expanded={aapen}
        onClick={onAapne}>
        <span className="loggbok-naar">{klokke(e.occurred_at, tz)}</span>
        <span className="grow">
          <strong>{navn}</strong>
          {svar && <> — {svar}</>}
          <span className="muted"> · {merder}</span>
        </span>
        {/* «Bekreftet» og «egen» er to påstander med ulik vekt, og de må ikke
            tegnes likt. Ordet, ikke en farge: det virker også for den som ikke
            skiller fargene. */}
        {e.via === 'bekreftet' && <span className="chip">bekreftet</span>}
        {e.severity === 'avvik' && <span className="chip">avvik</span>}
      </button>

      {aapen && (
        <div className="stack loggbok-radkropp">
          {e.note && <p>{e.note}</p>}
          <p className="muted">
            Ført av {e.recorded_by || 'ukjent'}
            {e.seen_by.length > 0 && ` · sett av ${e.seen_by.length}`}
          </p>

          {mmsi !== null && (
            <div className="loggbok-horrad">
              <Link className="btn sm"
                to={`/sites/${encodeURIComponent(id)}/loggbok/spor?on=${
                  encodeURIComponent(on)}&mmsi=${mmsi}`}>
                Se sporet
              </Link>
            </div>
          )}

          {e.deviation && (
            <p className="muted">
              Avvik — {e.deviation.utfylt} av 4 felt fylt ut.{' '}
              <Link to={`/sites/${encodeURIComponent(id)}/loggbok/uke`}>
                Fyll ut i ukesoppsummeringen
              </Link>
            </p>
          )}

          {e.comments.map((c) => (
            <p key={c.comment_id} className="loggbok-kommentar">
              <strong>{c.author}</strong> {c.body}
            </p>
          ))}

          <div className="row">
            <input value={kommentar} placeholder="Skriv en kommentar"
              onChange={(ev) => setKommentar(ev.target.value)} />
            <button type="button" className="btn sm"
              disabled={travel || !kommentar.trim()}
              onClick={async () => {
                if (await gjor(() => loggbok.kommentar(id, e.entry_id,
                  { on_date: on, body: kommentar.trim() }))) setKommentar('')
              }}>Send</button>
          </div>

          <div className="row">
            {!e.seen_by.length && (
              <button type="button" className="btn sm ghost" disabled={travel}
                onClick={() => gjor(() => loggbok.sett(id, e.entry_id,
                  { on_date: on }))}>Sett</button>
            )}
            <span className="grow" />
            {/* Stemplet, aldri slettet. Raden blir stående og slutter å telle,
                fordi en retting må fortsette å se ut som en retting. */}
            <button type="button" className="btn sm ghost danger" disabled={travel}
              onClick={() => gjor(() => loggbok.trekk(id, e.entry_id,
                { on_date: on }))}>Trekk tilbake</button>
          </div>
        </div>
      )}
    </article>
  )
}

/** Å skrive noe selv: velg hva, trykk ett svar, ferdig.
 *
 *  Ingen av de seks typene har et påkrevd tekstfelt. Det er ikke et
 *  utelatelsesvalg — det er regelen: et påkrevd tekstfelt er hvordan en runde
 *  på ett minutt blir en på tre, og deretter en som ikke blir gjort. */
function Skriv({ d, id, on, travel, gjor, onLukk }: {
  d: LoggbokDag
  id: string
  on: string
  travel: boolean
  gjor: (fn: () => Promise<LoggbokDag>) => Promise<boolean>
  onLukk: () => void
}) {
  const [kind, setKind] = useState<string | null>(null)
  const [answer, setAnswer] = useState<string | null>(null)
  const [merder, setMerder] = useState<string[]>(
    () => d.pens.map((p) => p.pen_id))
  const [notat, setNotat] = useState('')
  const [avvik, setAvvik] = useState(false)
  const [kategori, setKategori] = useState<string | null>(null)

  const spec = kind ? d.katalog.skrevet[kind] : null

  const lagre = async () => {
    if (!kind) return
    const ok = await gjor(() => loggbok.skriv(id, {
      on_date: on,
      kind,
      answer,
      pen_ids: merdliste(merder, d.pens),
      note: notat.trim() || null,
      severity: avvik ? 'avvik' : 'notat',
      category: avvik ? kategori : null,
    }))
    if (ok) onLukk()
  }

  return (
    <div className="card stack loggbok-skriv">
      <div className="loggbok-typer">
        {Object.entries(d.katalog.skrevet).map(([k, v]) => (
          <button key={k} type="button"
            className={`btn sm${kind === k ? ' primary' : ' ghost'}`}
            onClick={() => { setKind(k); setAnswer(null) }}>{v.navn}</button>
        ))}
      </div>

      {spec && (
        <>
          <Svarrad svar={spec.svar} valgt={answer} laast={travel}
            onSvar={(k) => setAnswer(answer === k ? null : k)} />
          <Merdvelger pens={d.pens} valgt={merder} onValg={setMerder} />
          <Notatfelt verdi={notat} onVerdi={setNotat} />

          {/* Ett trykk å melde som avvik. De fire analysefeltene åpner i
              ukesoppsummeringen, med vilje: å kreve rotårsak ved merdkanten er
              hvordan et avvik blir stående uregistrert i stedet for uferdig. */}
          <div className="row">
            <button type="button"
              className={`fchip${avvik ? ' paa' : ''}`} aria-pressed={avvik}
              onClick={() => setAvvik(!avvik)}>Dette var et avvik</button>
          </div>
          {avvik && (
            <Svarrad svar={d.katalog.avvik_kategorier} valgt={kategori}
              laast={travel} onSvar={setKategori} />
          )}

          <div className="row">
            <button type="button" className="btn primary"
              disabled={travel || (avvik && !kategori)}
              onClick={lagre}>Lagre</button>
            <button type="button" className="btn ghost"
              onClick={onLukk}>Avbryt</button>
          </div>
          {avvik && !kategori && (
            <p className="muted">
              Et avvik må ha en kategori — det er det som gjør at det kan telles
              og følges over tid.
            </p>
          )}
        </>
      )}
    </div>
  )
}
